import { useLiveSessions } from "@/hooks/call-center/useLiveSessions";
import { useWaitingRoomLeads } from "@/hooks/call-center/useWaitingRoomLeads";
import { useOtherAgentEngagements } from "@/hooks/call-center/useOtherAgentEngagements";
import useAuthUser from "@/hooks/useAuthUser";
import { useCallCentersStore } from "@/store/call-centers/call-centers.store";
import { useSocketContext } from "@/context/socket.context/socket.context";
import { TJoinedClient, TLiveAgent, TLiveSession } from "@/models/call.center.model";
import { getMyCurrentCallCenterStatus, notifyAgentStatusChanged } from "@/services/call-center/agents.service";
import { CALLCENTER_USER_STATUS_IN_CALL, CALLCENTER_USER_STATUS_READY, CUSTOM_EVENT_USER_STATUS_CHANGED } from "@icebergeradmin/common";
import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";
import * as Sentry from "@sentry/react";
import { setAgentAvailable } from "@/lib/agentAvailability";

type CallCenterProps = {
  liveCalls: TLiveSession[];
  joinedLeads: TJoinedClient[];
  /**
   * The agent's current call-center status. `undefined` while the initial
   * fetch is in flight — consumers that care about the distinction (status
   * dropdown, join-meeting gate) render their own inline loading state.
   */
  agentStatus?: number;
  isAgentAvailable: boolean;
  personalRoomSessionName: string;
  /** sessionName -> name of another agent currently calling that lead (voice). */
  engagedByOtherAgent: Record<string, string>;
  updateAgentStatus: (newStatus?: number) => void;
  removeWaitingRoomEntry: (entryId: string) => void;
};


const CallCenterContext = createContext<CallCenterProps>({
  liveCalls: [],
  joinedLeads: [],
  isAgentAvailable: false,
  personalRoomSessionName: "",
  engagedByOtherAgent: {},
  updateAgentStatus: () => undefined,
  removeWaitingRoomEntry: () => undefined,
});

const CallCenterProvider = ({ children }: PropsWithChildren) => {
  const { user } = useAuthUser();
  const socket = useSocketContext();
  const selectionReady = useCallCentersStore((s) => s.selectionReady);
  const selectedCallCenter = useCallCentersStore((s) => s.selectedCallCenter);
  const { joinedLeads, liveCalls } = useLiveSessions(selectionReady ? selectedCallCenter?.id : undefined);
  const [agentStatus, setAgentStatus] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  // "Agent" = someone who is assigned to at least one call center
  const isAgent = !!user.assignedCallCenterIds?.length

  const personalRoomSessionName =
    user?.companyUniqueName && user?.personalRoomId
      ? `${user.companyUniqueName}-${user.personalRoomId}`
      : "";

  const { waitingRoomLeads, removeEntry: removeWaitingRoomEntry } = useWaitingRoomLeads(personalRoomSessionName);
  const engagedByOtherAgent = useOtherAgentEngagements();

  const allJoinedLeads = useMemo(
    () => [...joinedLeads, ...waitingRoomLeads],
    [joinedLeads, waitingRoomLeads]
  );


  useEffect(() => {
    if (!isAgent) return setIsLoading(false);
    let mounted = true;
    getMyCurrentCallCenterStatus()
      .then((status) => {
        if (mounted) setAgentStatus(status);
      })
      .catch((err) => {
        // Degrade to "offline" (status 0) instead of blocking: without this
        // catch a failed fetch left isLoading true forever.
        Sentry.captureException(err, {
          tags: { component: "CallCenterContext", operation: "getMyCurrentCallCenterStatus" },
        });
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isAgent]);

  const updateAgentStatus = useCallback((newStatus?: number) => {
    if (newStatus === undefined) return;
    setAgentStatus((prev) => {
      if (prev === newStatus) return prev;
      notifyAgentStatusChanged(newStatus);
      return newStatus;
    });
  }, []);

  // ── Single source of truth across tabs/devices ───────────────────────
  // The backend broadcasts CUSTOM_EVENT_USER_STATUS_CHANGED to every socket
  // in the call center whenever an agent's status changes. We reconcile our
  // OWN status from that event so a change made in one tab (or another
  // device) is reflected everywhere — not just in the avatar list. This is
  // also why we keep the optimistic update in `updateAgentStatus`: the echo
  // for our own change just re-affirms the same value (React bails out).
  useEffect(() => {
    if (!isAgent || !user.id) return;
    const onStatusChanged = (agent: TLiveAgent) => {
      if (agent.id !== user.id || typeof agent.status !== "number") return;
      setAgentStatus(agent.status);
    };
    socket.on(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);
    return () => {
      socket.off(CUSTOM_EVENT_USER_STATUS_CHANGED, onStatusChanged);
    };
  }, [socket, isAgent, user.id]);

  const isAgentAvailable = agentStatus === CALLCENTER_USER_STATUS_READY || agentStatus === CALLCENTER_USER_STATUS_IN_CALL;

  useEffect(() => {
    if (!isLoading) setAgentAvailable(isAgentAvailable);
  }, [isAgentAvailable, isLoading]);

  // No render gate: this provider wraps DashboardLayout in the route tree, so
  // an early return here used to blank the ENTIRE screen (sidebar included)
  // behind an unstyled "Loading" div — the only module that didn't keep its
  // chrome during load. Children render immediately; while the status fetch
  // is in flight `agentStatus` is undefined and consumers show their own
  // inline loading state.
  const value = useMemo<CallCenterProps>(
    () => ({
      liveCalls,
      joinedLeads: allJoinedLeads,
      agentStatus: isLoading ? undefined : agentStatus,
      isAgentAvailable,
      personalRoomSessionName,
      engagedByOtherAgent,
      updateAgentStatus,
      removeWaitingRoomEntry,
    }),
    [
      liveCalls,
      allJoinedLeads,
      isLoading,
      agentStatus,
      isAgentAvailable,
      personalRoomSessionName,
      engagedByOtherAgent,
      updateAgentStatus,
      removeWaitingRoomEntry,
    ]
  );

  return (
    <CallCenterContext.Provider value={value}>
      {children}
    </CallCenterContext.Provider>
  );
};

export default CallCenterProvider;

export const useCallCenterContext = () => useContext(CallCenterContext);
